/*
  Detail Error Log of AJAX req
*/
import { ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons'

import keys from '@/config/keys'
import links from '@/config/links'
import store from '@/redux/store'
import getTerminology from './getTerminology'
import { routeTracker } from './routeTracker'
import { withAppRootPath } from './getAppRootPath'
import { logoutUser } from '@/redux/slices/authSlice'
import { sendWidgetReloadMessage } from './eldWidget'
import { WIDGET_SESSION_ERROR_CODE } from './widgetSession'

const commonMsg = 'Something went wrong. Check your internet connection and try again!'
const sessionExpTitle = `Session expired.`
const CHECKOUT_PATH_MARK = '/checkout'

const getBrowserPath = () =>
	`${window.location.pathname}${window.location.search}${window.location.hash}`

// Client-side navigation keeps this module alive, so hide the alert only
// while the browser is still on the page that failed.
let forcedErrorRedirectFrom = ''

export const isForcedErrorRedirect = () => {
	if (!forcedErrorRedirectFrom) return false
	if (getBrowserPath() !== forcedErrorRedirectFrom) {
		forcedErrorRedirectFrom = ''
		return false
	}
	return true
}

const isCheckoutPath = (pathname = '') => pathname.includes(CHECKOUT_PATH_MARK)

const getLocationPath = (location) => {
	if (!location?.pathname) return

	const queryParams = new URLSearchParams(location.search || '')
	queryParams.delete(keys.QUERY_PARAMS.USER_TOKEN)
	const search = queryParams.toString()

	return `${location.pathname}${search ? `?${search}` : ''}${location.hash || ''}`
}

const getLoginReturnPath = () => {
	if (!isCheckoutPath(window.location.pathname)) {
		return getLocationPath(routeTracker.getCurrent())
	}

	// Checkout without a cart errors after login. Return to the last real page.
	const returnLocation = [...routeTracker.getHistory()]
		.reverse()
		.find((loc) => loc?.pathname && !isCheckoutPath(loc.pathname))

	return getLocationPath(returnLocation)
}

const withQueryParam = (path, key, value) => {
	if (!path || !value) return path
	const absolute = /^(https?:)?\/\//i.test(path)
	const url = new URL(path, 'https://emma.local')
	url.searchParams.set(key, value)
	return absolute ? url.href : `${url.pathname}${url.search}${url.hash}`
}

const goToOkRoute = (route) => {
	if (!route) return

	let dest = route
	if (dest.includes(links.login.path)) {
		dest = withQueryParam(dest, keys.QUERY_PARAMS.REDIRECT, getLoginReturnPath())
		store.dispatch(logoutUser())
	}

	window.location.assign(withAppRootPath(dest))
}

const handleError = (error, notify = false, filename, args, staticFunctionAntd) => {
	/* Manually canceled due to another req started */
	const isManuallyCancelled = error?.code === 'ERR_CANCELED' && error?.name === 'CanceledError'
	if (isManuallyCancelled) return

	console.log(`=========AJAX Req Error Info${filename ? ` (File: ${filename})` : ''}============`)

	const { modal, notification } = staticFunctionAntd
	const { asNotification = false, ...popupProps } = args || {}

	let title = 'ERROR'
	let msg = error || commonMsg
	let statusCode
	let okLabel
	let okRoute
	let forceRedirect = false
	let onOk
	let requestId
	let traceId

	if (error instanceof CustomError) {
		console.error(
			`${error.name}: ${error.title ? `[${error.title}] ` : ''}${error.message} (${error.code})`,
		)
		if (error.title) title = error.title
		if (error.message) msg = error.message
	}

	if (error.response) {
		statusCode = error.response.status
		console.error('Error Data: ', error.response.data)
		console.error('Error Status Code: ', statusCode)
		console.error('Error Headers: ', error.response.headers)
		// Server responded with a status code that falls out of the range of 2xx

		const contentType = error.response.headers?.['content-type'] ?? ''
		if (contentType.includes('application/problem+json')) {
			// RFC 7807 Problem Details
			const {
				title: problemTitle,
				detail,
				requestId: reqId,
				traceId: trId,
				redirect_route,
				redirect_force,
			} = error.response.data || {}
			if (problemTitle) title = problemTitle
			if (detail) msg = detail
			if (reqId) requestId = reqId
			if (trId) traceId = trId
			if (redirect_route) {
				okRoute = redirect_route
				if (redirect_force === true) forceRedirect = true
			}
		} else {
			const { error: errorMsg, error_description, message, data: error_data } = error.response.data
			const { error_header, button_label, redirect_route, redirect_force } = error_data || {}

			if (button_label) okLabel = button_label
			if (redirect_route) {
				okRoute = redirect_route
				if (redirect_force === true) forceRedirect = true
			}

			if (errorMsg) {
				msg = errorMsg
				if (msg === 'invalid_token' && error.response.status === 401) {
					// Remove token from local storage
					localStorage.removeItem(keys.ACCESS_TOKEN)
					// Reload to redirect the user to the login page
					setTimeout(() => {
						window[keys.APP_NAVIGATE](links.login.path)
						window.location.reload()
					}, 2000)
					title = 'Account archived'
					msg = 'Please contact support.'
				} else if (msg === 'invalid_grant') {
					msg =
						'Refresh token is invalid, expired, revoked, does not match the redirection URI used in the authorization request, or was issued to another client.'
					// Remove token from local storage
					localStorage.removeItem(keys.ACCESS_TOKEN)
				} else if (msg === 'untrusted_source') {
					msg = error_description
				}
			} else if (statusCode === 401) {
				// In other tab user logged out / bb_session table is dropped
				msg = `Your session has expired, as a security measure you have been logged out. Click 'OK' to log back in.`
				onOk = () => {
					window[keys.APP_NAVIGATE](links.login.path)
					window.location.reload()
				}
			} else if (statusCode === 404) {
				msg = "Sorry, can't find the data you're looking for."
			} else if (message) {
				msg = message
			}

			if (error_header) {
				title = error_header
			} else {
				switch (statusCode) {
					case 400:
						title = `Validation Error`
						break
					case 401:
						title = sessionExpTitle
						break
					case 403:
						title = `Permission Error`
						break
					case 404:
						title = `Not Found Error`
						break
					default:
						// 500 or anything else
						title = `System Error`
				}
			}
		}

		// Unified widget session expiry guard — runs after both content-type branches.
		// Placing it here ensures it overrides the generic 401 handler in the else
		// branch (which would otherwise navigate to /login inside the widget).
		// Works regardless of whether the server uses application/problem+json or
		// plain application/json for this error.
		const isWidgetMode = store.getState().theme?.embedView === keys.EMBED_VIEW.widget
		if (
			statusCode === 401 &&
			error.response?.data?.code === WIDGET_SESSION_ERROR_CODE &&
			isWidgetMode
		) {
			if (error.response?.data?.title) title = error.response.data.title
			if (error.response?.data?.detail) msg = error.response.data.detail
			okRoute = undefined
			forceRedirect = false
			onOk = () => sendWidgetReloadMessage()
		}
	} else if (error.request) {
		// The request was made but no response was received
		// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
		// http.ClientRequest in node.js
		console.error('Error: no response was received\n------------------\n', error.request)
	} else {
		// Something happened in setting up the request that triggered an Error
		console.error('Error: setting up the request\n-------------------\n', error.message)
		if (error.message) msg = error.message
	}
	console.warn('Error Config:\n-----------------\n', error.config)
	console.warn('==============End Error Info=============')

	const finalMsg = String(msg || '')
		.replace(/ERROR Error:|Error:/g, '')
		.replace(/\n/g, '<br/>')

	// redirect_force means leave now, before any error UI.
	if (forceRedirect && okRoute) {
		forcedErrorRedirectFrom = getBrowserPath()
		goToOkRoute(okRoute)
		return finalMsg
	}

	if (finalMsg && notify) {
		const bodyEl = <div dangerouslySetInnerHTML={{ __html: finalMsg }} />
		if (asNotification) {
			notification.error({
				message: title,
				description: bodyEl,
				duration: 6,
				...popupProps,
			})
		} else {
			const showModal = () => {
				if (statusCode === 503) return

				if (statusCode === 401) {
					const el = document.getElementById(keys.INACTIVITY_MODAL_CLOSE_TRIGGER_BTN_ID)
					el && el.click()
				}

				const popupTime = new Date().toISOString()
				const isConflict = statusCode === 409

				const TRACE_FOOTER =
					requestId || traceId ? (
						<div className='mt-3 max-w-full px-2 text-center text-[11px] font-medium leading-tight text-gray-400'>
							<div>{popupTime}</div>
							<div className='mt-1 break-all'>
								{requestId && <span>Request ID: {requestId}</span>}
								{requestId && traceId && <span className='mx-1'>|</span>}
								{traceId && <span>Trace ID: {traceId}</span>}
							</div>
						</div>
					) : null

				modal.confirm({
					className: `emma-popup-confirm-modal ${keys.C_APP_ERROR_POPUP_CLASS} error-code-${
						statusCode || ''
					}`,
					title: <b>{title}</b>,
					icon: <ExclamationCircleOutlined />,
					content: (
						<>
							{bodyEl}
							{!isConflict && TRACE_FOOTER}
						</>
					),
					okText: (
						<>
							{okLabel || getTerminology('ok', null, false)} <RightOutlined />
						</>
					),
					onOk: (close) => {
						close?.()
						goToOkRoute(okRoute)
						onOk?.()
					},
					okButtonProps: { shape: 'round' },
					cancelButtonProps: { style: { opacity: 0, pointerEvents: 'none' } },
				})
			}

			if ([commonMsg].includes(finalMsg) || [sessionExpTitle].includes(title)) {
				// Check if it's already showing
				const el = document.querySelector(`.${keys.C_APP_ERROR_POPUP_CLASS}`)
				if (!el) showModal()
			} else {
				showModal()
			}
		}
	}
	return finalMsg
}

export default handleError

export class CustomError extends Error {
	constructor(message, code, title) {
		super(message)
		this.name = this.constructor.name
		this.code = code
		this.title = title
		// Ensure correct prototype chain
		Object.setPrototypeOf(this, new.target.prototype)
	}
}
